import type { ConsoleMe } from '@plato/shared';
import { useCallback, useEffect, useState } from 'react';
import { forgetInvite, peekInvite, rememberInviteFromUrl } from './auth/invite-token.ts';
import { NoAccessPage } from './auth/no-access-page.tsx';
import { SignInPage } from './auth/sign-in-page.tsx';
import type { ConsoleAuth } from './data/console-auth.ts';
import { ConsoleProvider, useHasRole } from './data/console-context.tsx';
import type { ConsoleData, TenantSummary } from './data/console-data.ts';
import { Header } from './shell/header.tsx';
import { Sidebar } from './shell/sidebar.tsx';
import { useCurrentSection } from './shell/use-current-section.ts';
import { TryItPanel } from './try-it/try-it-panel.tsx';

type Session =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'signed-out' }
  | { readonly status: 'signed-in'; readonly me: ConsoleMe; readonly inviteError: string | null };

export function App({ data, auth }: { data: ConsoleData; auth: ConsoleAuth }) {
  const { session, refresh } = useSession(auth);

  if (session.status === 'loading') return <p role="status">Loading…</p>;
  if (session.status === 'error') return <p role="alert">{session.message}</p>;
  if (session.status === 'signed-out') {
    return <SignInPage auth={auth} onSignedIn={refresh} hasInvite={peekInvite() !== null} />;
  }
  const signOut = () => void auth.signOut().then(refresh);
  const inviteRefused = session.inviteError !== null && (
    <p role="alert" className="notice notice-danger banner">
      {session.inviteError}
    </p>
  );
  return (
    <>
      {inviteRefused}
      {session.me.tenants.length === 0 ? (
        <NoAccessPage me={session.me} onSignOut={signOut} />
      ) : (
        <ConsoleShell data={data} me={session.me} onSignOut={signOut} />
      )}
    </>
  );
}

function ConsoleShell(props: { data: ConsoleData; me: ConsoleMe; onSignOut: () => void }) {
  const tenants: TenantSummary[] = props.me.tenants.map((tenant) => ({
    id: tenant.id,
    name: tenant.name,
    role: tenant.role,
    via: tenant.via,
  }));
  const [tenantId, setTenantId] = useState<string | null>(null);
  const section = useCurrentSection();
  const tenant = tenants.find((candidate) => candidate.id === tenantId) ?? tenants[0];
  if (tenant === undefined) return null;

  return (
    <ConsoleProvider data={props.data} tenant={tenant}>
      <ConsoleLayout
        key={tenant.id}
        tenants={tenants}
        tenant={tenant}
        onSwitch={setTenantId}
        me={props.me}
        demo={props.data.demo}
        onSignOut={props.onSignOut}
        section={section}
      />
    </ConsoleProvider>
  );
}

function ConsoleLayout(props: {
  tenants: TenantSummary[];
  tenant: TenantSummary;
  onSwitch: (tenantId: string) => void;
  me: ConsoleMe;
  demo: boolean;
  onSignOut: () => void;
  section: ReturnType<typeof useCurrentSection>;
}) {
  const canTry = useHasRole('editor');
  const [tryIt, setTryIt] = useState<'never' | 'open' | 'closed'>('never');
  const { section } = props;
  return (
    <div className={tryIt === 'open' ? 'app with-try-it' : 'app'}>
      <Sidebar current={section.id} />
      <div className="app-main">
        <Header
          tenants={props.tenants}
          current={props.tenant}
          onSwitch={props.onSwitch}
          account={props.me.account}
          demo={props.demo}
          onSignOut={props.onSignOut}
          onTryIt={canTry ? () => setTryIt(tryIt === 'open' ? 'closed' : 'open') : undefined}
        />
        <main aria-labelledby="page-title">
          <section.Component />
        </main>
      </div>
      {canTry && tryIt !== 'never' && (
        <TryItPanel hidden={tryIt !== 'open'} onClose={() => setTryIt('closed')} />
      )}
    </div>
  );
}

function useSession(auth: ConsoleAuth): { session: Session; refresh: () => void } {
  const [session, setSession] = useState<Session>({ status: 'loading' });
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `version` is the refresh trigger.
  useEffect(() => {
    rememberInviteFromUrl();
    let current = true;
    void (async () => {
      try {
        const me = await auth.getMe();
        if (me === null) {
          if (current) setSession({ status: 'signed-out' });
          return;
        }
        const inviteError = await acceptPendingInvite(auth);
        const latest = inviteError === undefined ? me : ((await auth.getMe()) ?? me);
        if (current) {
          setSession({ status: 'signed-in', me: latest, inviteError: inviteError ?? null });
        }
      } catch (cause) {
        if (current) {
          setSession({
            status: 'error',
            message: cause instanceof Error ? cause.message : 'Could not reach the console API.',
          });
        }
      }
    })();
    return () => {
      current = false;
    };
  }, [auth, version]);

  return { session, refresh };
}

async function acceptPendingInvite(auth: ConsoleAuth): Promise<string | null | undefined> {
  const token = peekInvite();
  if (token === null) return undefined;
  forgetInvite();
  try {
    await auth.acceptInvite(token);
    return null;
  } catch (cause) {
    return cause instanceof Error ? cause.message : 'The invite could not be accepted.';
  }
}
